import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

/**
 * Firebase-native production error monitoring.
 *
 * Errors are written to the `errorReports` Firestore collection (create-only
 * for clients, readable by admins — see firestore.rules). View them in the
 * Firebase console under Firestore > errorReports, newest first by `createdAt`.
 */

export interface ErrorContext {
  /** Where the error was caught, e.g. "ErrorBoundary", "window.onerror" */
  source: string;
  componentStack?: string;
  extra?: Record<string, unknown>;
}

const MAX_REPORTS_PER_SESSION = 20;
const MAX_FIELD_LENGTH = 4000;

let reportCount = 0;
const reportedKeys = new Set<string>();

const truncate = (value: string): string =>
  value.length > MAX_FIELD_LENGTH ? value.slice(0, MAX_FIELD_LENGTH) : value;

const toErrorParts = (error: unknown): { message: string; stack: string } => {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack || "" };
  }
  if (typeof error === "string") {
    return { message: error, stack: "" };
  }
  try {
    return { message: JSON.stringify(error), stack: "" };
  } catch {
    return { message: String(error), stack: "" };
  }
};

/**
 * Report an error to Firestore. Never throws; deduplicates identical errors
 * and caps reports per session so a render loop can't flood the collection.
 */
export async function reportError(
  error: unknown,
  context: ErrorContext
): Promise<void> {
  try {
    const { message, stack } = toErrorParts(error);

    if (process.env.NODE_ENV !== "production") {
      console.error(`[errorReporting:${context.source}]`, error);
      return;
    }

    const dedupeKey = `${context.source}:${message}`;
    if (reportedKeys.has(dedupeKey) || reportCount >= MAX_REPORTS_PER_SESSION) {
      return;
    }
    reportedKeys.add(dedupeKey);
    reportCount++;

    await addDoc(collection(db, "errorReports"), {
      message: truncate(message),
      stack: truncate(stack),
      source: context.source,
      componentStack: truncate(context.componentStack || ""),
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: auth.currentUser?.uid || null,
      createdAt: serverTimestamp(),
    });
  } catch {
    // The error reporter must never become a second source of errors.
  }
}

/**
 * Install window-level handlers for uncaught errors and unhandled promise
 * rejections. Call once at app startup.
 */
export function initGlobalErrorReporting(): void {
  window.addEventListener("error", (event) => {
    reportError(event.error ?? event.message, { source: "window.onerror" });
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason, { source: "unhandledrejection" });
  });
}

/** Test-only: reset session dedupe/cap state. */
export function _resetErrorReportingForTests(): void {
  reportCount = 0;
  reportedKeys.clear();
}
